































































































































































































































































































































































































































































































































































































import { Component, Vue, Watch, Prop } from "vue-property-decorator";
import moment from "moment";
import { isAdmin } from "@/common/role_utils";
import { ScheduleTimeOff, ScheduleTimeOffRequest, TimeOffStatus } from "@/api";

@Component
export default class TimeOffRequestTab extends Vue {
  @Prop({ default: false }) isActive!: boolean;

  loading = false;
  saving = false;
  deleting = false;
  accepting = false;
  rejecting = false;
  formValid = false;
  rejectFormValid = false;

  // Filter state
  filterStartDate = moment()
    .startOf("month")
    .format("YYYY-MM-DD");
  filterEndDate = moment()
    .endOf("month")
    .format("YYYY-MM-DD");
  filterStatus = "";
  startDateMenu = false;
  endDateMenu = false;

  // Add Dialog
  addDialog = false;
  dateMode: "single" | "range" = "single";
  formDateMenu = false;
  formStartMenu = false;
  formEndMenu = false;

  form: {
    date: string;
    startDate: string;
    endDate: string;
    reason: string;
  } = {
    date: "",
    startDate: "",
    endDate: "",
    reason: "",
  };

  // Delete Dialog
  deleteDialog = false;
  selectedRequest: ScheduleTimeOff | null = null;

  // Accept Dialog
  acceptDialog = false;

  // Reject Dialog
  rejectDialog = false;
  rejectReason = "";

  statusOptions = [
    { text: "Pending", value: TimeOffStatus.PENDING },
    { text: "Approved", value: TimeOffStatus.ACCEPTED },
    { text: "Denied", value: TimeOffStatus.REJECTED },
    { text: "Cancelled", value: TimeOffStatus.CANCELLED },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  expanded: any[] = [];

  get headers() {
    const base = [];

    // Expand toggle column
    base.push({
      text: "",
      value: "data-table-expand",
      sortable: false,
      width: "40px",
    });

    // Employee column only for admin/owner
    if (this.isAdminOrOwner) {
      base.push({
        text: "Employee",
        value: "employeeName",
        sortable: false,
        width: "200px",
      });
    }

    base.push(
      { text: "Date", value: "date", sortable: true },
      { text: "Reason", value: "reason", sortable: false },
      { text: "Status", value: "status", sortable: true },
      { text: "Submitted On", value: "createdAt", sortable: true },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {
        text: "Actions",
        value: "actions",
        sortable: false,
        align: "center",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any,
    );

    return base;
  }

  get today(): string {
    return moment().format("YYYY-MM-DD");
  }

  get timeOffRequests(): ScheduleTimeOff[] {
    return this.$store.state.schedule.timeOffRequests || [];
  }

  get filteredRequests(): ScheduleTimeOff[] {
    let list = [...this.timeOffRequests];

    if (this.filterStartDate) {
      list = list.filter(r => r.date >= this.filterStartDate);
    }
    if (this.filterEndDate) {
      list = list.filter(r => r.date <= this.filterEndDate);
    }
    if (this.filterStatus) {
      list = list.filter(r => r.status === this.filterStatus);
    }

    return list.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }

  get isAdminOrOwner(): boolean {
    return isAdmin();
  }

  get selfEmployee() {
    return this.$store.getters["getCurrentEmployeeSelf"];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get allEmployees(): any[] {
    return this.$store.state.directory.employees || [];
  }

  @Watch("isActive")
  onActiveChange(active: boolean) {
    if (active) this.fetchData();
  }

  async mounted() {
    if (this.isActive) {
      await this.fetchData();
    }
  }

  async fetchData() {
    this.loading = true;
    try {
      const startDate =
        this.filterStartDate ||
        moment()
          .startOf("month")
          .format("YYYY-MM-DD");
      const endDate =
        this.filterEndDate ||
        moment()
          .endOf("month")
          .format("YYYY-MM-DD");

      const fetches = [
        this.$store.dispatch("schedule/fetchTimeOffRequests", {
          startDate,
          endDate,
        }),
      ];

      // Admin also needs the employee list for names
      if (this.isAdminOrOwner && this.allEmployees.length === 0) {
        fetches.push(this.$store.dispatch("directory/loadAllEmployees"));
      }

      await Promise.all(fetches);
    } catch (error) {
      console.error("Error fetching time off requests:", error);
    } finally {
      this.loading = false;
    }
  }

  @Watch("filterStartDate")
  @Watch("filterEndDate")
  onFilterChange() {
    this.fetchData();
  }

  // ─── Employee helpers ───────────────────────────────────────────────────────

  getEmployeeName(employeeId: number): string {
    const emp = this.allEmployees.find(e => e.id === employeeId);
    if (!emp) return `Employee #${employeeId}`;
    const first = emp.firstName || emp.first_name || "";
    const last = emp.lastName || emp.last_name || "";
    return `${first} ${last}`.trim() || `Employee #${employeeId}`;
  }

  getEmployeeInitials(employeeId: number): string {
    const name = this.getEmployeeName(employeeId);
    if (name.startsWith("Employee #")) return "?";
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }

  getEmployeeColor(employeeId: number): string {
    const emp = this.allEmployees.find(e => e.id === employeeId);
    return emp?.color || "#1969db";
  }

  // ─── Formatters ─────────────────────────────────────────────────────────────

  capitalizeStatus(value: string): string {
    if (!value) return "";
    if (value === TimeOffStatus.ACCEPTED) return "Approved";
    if (value === TimeOffStatus.REJECTED) return "Denied";
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return "—";
    return moment(dateStr).format("MMM D, YYYY");
  }

  statusColor(status: string): string {
    switch (status) {
      case TimeOffStatus.PENDING:
        return "amber lighten-4";
      case TimeOffStatus.ACCEPTED:
        return "green lighten-4";
      case TimeOffStatus.REJECTED:
        return "red lighten-4";
      case TimeOffStatus.CANCELLED:
        return "grey lighten-3";
      default:
        return "grey lighten-3";
    }
  }

  statusTextColor(status: string): string {
    switch (status) {
      case TimeOffStatus.PENDING:
        return "amber darken-4";
      case TimeOffStatus.ACCEPTED:
        return "green darken-3";
      case TimeOffStatus.REJECTED:
        return "red darken-3";
      case TimeOffStatus.CANCELLED:
        return "grey darken-2";
      default:
        return "grey darken-2";
    }
  }

  // ─── Add Dialog ─────────────────────────────────────────────────────────────

  openAddDialog() {
    this.resetForm();
    this.addDialog = true;
  }

  closeAddDialog() {
    this.addDialog = false;
    this.resetForm();
  }

  resetForm() {
    this.dateMode = "single";
    this.form = { date: "", startDate: "", endDate: "", reason: "" };
    this.formValid = false;
    const formRef = this.$refs.addForm as Vue & {
      resetValidation: () => void;
    };
    if (formRef) formRef.resetValidation();
  }

  async submitRequest() {
    const formRef = this.$refs.addForm as Vue & { validate: () => boolean };
    if (!formRef.validate()) return;

    const employeeId = this.selfEmployee?.id;
    if (!employeeId) {
      this.$store.commit("alert/SET_SHOW_ERROR", "Employee ID not found.", {
        root: true,
      });
      return;
    }

    this.saving = true;
    try {
      const requestBody: ScheduleTimeOffRequest = { employeeId };

      if (this.dateMode === "single") {
        requestBody.date = this.form.date;
      } else {
        requestBody.startDate = this.form.startDate;
        requestBody.endDate = this.form.endDate;
      }

      if (this.form.reason.trim()) {
        requestBody.reason = this.form.reason.trim();
      }

      await this.$store.dispatch("schedule/createTimeOff", requestBody);
      this.closeAddDialog();
      await this.fetchData();
    } catch (error) {
      console.error("Error submitting time off request:", error);
    } finally {
      this.saving = false;
    }
  }

  // ─── Delete (employee cancel) ────────────────────────────────────────────────

  confirmDelete(request: ScheduleTimeOff) {
    this.selectedRequest = request;
    this.deleteDialog = true;
  }

  async deleteRequest() {
    if (!this.selectedRequest) return;
    this.deleting = true;
    try {
      await this.$store.dispatch(
        "schedule/deleteTimeOff",
        this.selectedRequest.id,
      );
      this.deleteDialog = false;
      this.selectedRequest = null;
    } catch (error) {
      console.error("Error deleting time off request:", error);
    } finally {
      this.deleting = false;
    }
  }

  // ─── Accept ─────────────────────────────────────────────────────────────────

  confirmAccept(request: ScheduleTimeOff) {
    this.selectedRequest = request;
    this.acceptDialog = true;
  }

  async acceptRequest() {
    if (!this.selectedRequest) return;
    this.accepting = true;
    try {
      await this.$store.dispatch(
        "schedule/acceptTimeOff",
        this.selectedRequest.id,
      );
      this.acceptDialog = false;
      this.selectedRequest = null;
    } catch (error) {
      console.error("Error accepting time off request:", error);
    } finally {
      this.accepting = false;
    }
  }

  // ─── Reject ─────────────────────────────────────────────────────────────────

  openRejectDialog(request: ScheduleTimeOff) {
    this.selectedRequest = request;
    this.rejectReason = "";
    this.rejectFormValid = false;
    const formRef = this.$refs.rejectForm as Vue & {
      resetValidation: () => void;
    };
    if (formRef) formRef.resetValidation();
    this.rejectDialog = true;
  }

  closeRejectDialog() {
    this.rejectDialog = false;
    this.rejectReason = "";
    this.selectedRequest = null;
  }

  async rejectRequest() {
    const formRef = this.$refs.rejectForm as Vue & { validate: () => boolean };
    if (!formRef.validate()) return;

    if (!this.selectedRequest) return;
    this.rejecting = true;
    try {
      await this.$store.dispatch("schedule/rejectTimeOff", {
        id: this.selectedRequest.id,
        requestBody: { rejectReason: this.rejectReason.trim() },
      });
      this.closeRejectDialog();
    } catch (error) {
      console.error("Error rejecting time off request:", error);
    } finally {
      this.rejecting = false;
    }
  }
}
